import { useDispatch, useSelector } from "src/redux/hooks";
import { setSectionProperty } from "src/redux/slices/generatorSlice";
import { useCallback, useEffect, useRef } from "react";
import { IRow } from "src/interfaces";

import { isSavedStateMarkingSuspended } from "./savedStateGuard";

/**
 * Hook to manage the 'saved' state of cells in a row.
 * When a cell value changes, sets saved: false on all cells in that row (if grayOldRows is enabled).
 */
export const useMarkRowUnsaved = (sectionIndex: number, rowIndex: number) => {
  const dispatch = useDispatch();
  const section = useSelector(
    state => state.generatorSlice?.lbentry?.sections?.[sectionIndex],
  );

  const isInvalid =
    typeof sectionIndex !== "number" ||
    typeof rowIndex !== "number" ||
    !section;

  const grayOldRows = section?.grayOldRows;
  const rows = section?.rows;
  const savedRows = section?.savedRows;

  const rowsRef = useRef(rows);
  const savedRowsRef = useRef(savedRows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    savedRowsRef.current = savedRows;
  }, [savedRows]);

  /**
   * Marks the row as not saved in the savedRows array
   * Optimization: Uses section-level savedRows boolean array instead of modifying row structure
   */
  const markRowAsUnsaved = useCallback(
    (passedRow?: IRow) => {
      if (isInvalid || !grayOldRows) return;
      // Skip during programmatic bulk updates (e.g. NMEA accept) so rows that
      // received no real user edit are not ungrayed.
      if (isSavedStateMarkingSuspended()) return;

      const row = passedRow ?? rowsRef.current?.[rowIndex];
      if (!row) return;

      const currentSavedRows = savedRowsRef.current;
      if (!currentSavedRows || !currentSavedRows[rowIndex]) return;

      const newSavedRows = [...currentSavedRows];
      newSavedRows[rowIndex] = false;

      dispatch(
        setSectionProperty({
          sectionIndex,
          property: { savedRows: newSavedRows },
        }),
      );
    },
    [isInvalid, grayOldRows, rowIndex, sectionIndex, dispatch],
  );

  return { markRowAsUnsaved };
};
